import React, { lazy, useEffect } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { ThemeProvider } from "@/hooks/layout/use-theme";
import { NotificationProvider } from "@/contexts/notification-context";
import { TaskProvider } from "@/contexts/task-context";
// Removed TokenProvider - using direct database queries instead
import { AIAgentChatProvider } from "@/contexts/ai-agents";
import { AuthProvider } from "@/contexts/auth";
import { DragProvider } from "@/contexts/drag/DragContext";
import { GlobalModalsProvider } from "@/contexts/GlobalModalsContext";
import RequireAuth from "@/components/auth/RequireAuth";
import UnifiedDashboardLayout from "@/components/dashboard/UnifiedDashboardLayout";
import GlobalModalsContainer from "@/components/modals/GlobalModalsContainer";
import { DatabaseCacheInitializer } from "@/components/auth/DatabaseCacheInitializer";

import { UniversalLibraryProvider } from "@/contexts/UniversalLibraryContext";
import { UniversalLibraryErrorBoundary } from "@/contexts/UniversalLibraryContext/ErrorBoundary";
import { QuestProvider } from "@/contexts/QuestContext";

// Auth Pages
import AuthPage from "@/pages/auth/AuthPage";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AuthConfirm from "@/pages/auth/AuthConfirm";
import AcceptInvite from "@/pages/auth/AcceptInvite";
import Unauthorized from "@/pages/auth/Unauthorized";
import Welcome from "@/pages/Welcome";

// Dashboard Components  
import DashboardHome from "@/pages/Home";
import Profile from "@/pages/account/Profile";

import AIAgentSettings from "@/components/ai-agents/settings/AIAgentSettings";

// Productivity Apps
import TaskManager from "@/pages/apps/productivity/TaskManager"; 
import Contacts from "@/pages/apps/productivity/Contacts";
import Projects from "@/pages/apps/productivity/Projects";
import ProjectDetails from "@/pages/apps/productivity/ProjectDetails";
import Pipeline from "@/pages/apps/productivity/Pipeline";

// Creative Apps
import ImageGenerator from "@/pages/apps/creative/ImageGenerator";
import PrivateImageGenerator from "@/pages/apps/creative/PrivateImageGenerator";
import VideoGenerator from "@/pages/VideoGenerator";
import Workdesk from "@/pages/apps/creative/ContentGenerator";

// Education Apps
import ResearchGenerator from "@/pages/apps/education/ResearchGenerator";

// Private generators 
import PrivateResearchGenerator from "@/pages/apps/creative/PrivateResearchGenerator";
import PrivateTextGenerator from "@/pages/apps/creative/PrivateTextGenerator";

// Community
import Community from "@/pages/Community";
import PostThread from "@/pages/DiscussionThread";

import LicensePage from "@/components/image-generator/license";
import Subscription from "@/pages/account/Subscription";
// Removed UsagePage - merged into Billing page
import WorkbookTabbedEditor from "@/components/content-generator/editor/WorkbookTabbedEditor";
import ProjectLayout from "@/components/project/ProjectLayout";
import ProjectTasksPage from "@/components/project/ProjectTasksPage";
import ProjectSettingsPage from "@/components/project/ProjectSettingsPage";
import ProjectPageEditorPage from "@/components/project/ProjectPageEditorPage";
import ProjectResourcesPage from "@/components/project/ProjectResourcesPage";
import ProjectImagesPage from "@/components/project/ProjectImagesPage";
import ProjectPagesPage from "@/components/project/ProjectPagesPage";
import ProjectServicesPage from "@/components/project/ProjectServicesPage";
import LegacyProjectRedirect from "@/components/project/LegacyProjectRedirect";

// Import App Library directly instead of lazy loading to avoid Suspense issues
import AppLibraryPage from '@/pages/apps/AppLibrary';

// Import ErrorTest component for testing the error logger
import ErrorTest from '@/components/ErrorTest';

// Other Dashboard Pages
import Notifications from "@/pages/apps/Notifications";
import UniversalStoragePage from '@/pages/UniversalStorage';

// Create a client with improved caching defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  return (
    <React.StrictMode>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <DatabaseCacheInitializer>
              <NotificationProvider>
                <GlobalModalsProvider>
                  <AIAgentChatProvider>
                      <TaskProvider>
                        <DragProvider>
                          <TooltipProvider>
                          <Toaster />
                          <BrowserRouter>
                          <Routes>
                            {/* Root path - redirect to signin when logged out, home when logged in */}
                            <Route path="/" element={<Navigate to="/signin" replace />} />
                            
                            {/* Auth Routes - No longer need CaptchaProvider */}
                            <Route path="/signin" element={<AuthPage />} />
                            <Route path="/verify-email" element={<VerifyEmail />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/auth/confirm" element={<AuthConfirm />} />
                            <Route path="/auth/accept-invite" element={<AcceptInvite />} />
                            <Route path="/unauthorized" element={<Unauthorized />} />
                            <Route path="/welcome" element={<Welcome />} />
                            
                            {/* All Dashboard Routes - With Universal Library Provider */}
                            <Route path="/*" element={
                              <RequireAuth>
                                <UniversalLibraryErrorBoundary>
                                  <UniversalLibraryProvider>
                                    <QuestProvider>
                                      <UnifiedDashboardLayout />
                                    </QuestProvider>
                                  </UniversalLibraryProvider>
                                </UniversalLibraryErrorBoundary>
                              </RequireAuth>
                            }>
                              {/* Home Route */}
                              <Route path="home" element={<DashboardHome />} />

                              {/* Storage Route */}
                              <Route path="storage" element={<UniversalStoragePage />} />

                              {/* Testing Route for Welcome component */}
                              <Route path="onboard" element={<Welcome />} />

                              {/* Generator Routes */}
                              <Route path="research-generator" element={<ResearchGenerator />} />
                              <Route path="private-research-generator" element={<PrivateResearchGenerator />} />
                              <Route path="private-text-generator" element={<PrivateTextGenerator />} />
                              
                              <Route path="image-generator" element={<ImageGenerator />} />
              <Route path="image-generator/license" element={<LicensePage />} />
              
              <Route path="private-image-generator" element={<PrivateImageGenerator />} />
              
              <Route path="video-generator" element={<VideoGenerator />} />
              
              {/* Public Library Routes */}
              <Route path="public-image-library" element={<ImageGenerator />} />
              <Route path="public-research-library" element={<ResearchGenerator />} />
                              
                              <Route path="project-manager" element={<Workdesk />} />
                              {/* Legacy route with redirect to new structure */}
                              <Route path="project-manager/edit/:pageId" element={<LegacyProjectRedirect />} />
                              
                              {/* New Nested Project Routes */}
                              <Route path="project/:id/*" element={<ProjectLayout />}>
                                <Route index element={<Navigate to="tasks" replace />} />
                                <Route path="tasks" element={<ProjectTasksPage />} />
                                <Route path="settings" element={<ProjectSettingsPage />} />
                                <Route path="page/:pageId" element={<ProjectPageEditorPage />} />
                                <Route path="resources" element={<ProjectResourcesPage />} />
                                <Route path="images" element={<ProjectImagesPage />} />
                                <Route path="pages" element={<ProjectPagesPage />} />
                                <Route path="services" element={<ProjectServicesPage />} />
                              </Route>
                              
                              {/* Productivity Routes */}
                              <Route path="task-manager" element={<TaskManager />} />
                              
                              <Route path="contacts" element={<Contacts />} />
                              
                              <Route path="projects" element={<Projects />} />
                              <Route path="projects/:projectId" element={<ProjectDetails />} />
                              
                              <Route path="pipeline" element={<Pipeline />} />
                              
                              <Route path="community" element={<Community />} />
                              <Route path="community/post/:id" element={<PostThread />} />
                              
                              <Route path="notifications" element={<Notifications />} />

                              <Route path="app-library" element={<AppLibraryPage />} />

                              {/* Account Routes */}
                              <Route path="account" element={<Profile />} />
                              <Route path="account/ai-agents" element={<AIAgentSettings />} />
                              <Route path="billing" element={<Subscription />} />
                              {/* Redirect old usage route to billing */}
                              <Route path="usage" element={<Navigate to="/billing" replace />} />
                              
                  

                              <Route path="error-test" element={<ErrorTest />} />
                            </Route>
                            
                            {/* Catch-all route */}
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                          
                          {/* Global Modals Container - Now inside Router context for smooth navigation */}
                          <GlobalModalsContainer />
                        </BrowserRouter>
                      </TooltipProvider>
                    </DragProvider>
                  </TaskProvider>
                </AIAgentChatProvider>
              </GlobalModalsProvider>
            </NotificationProvider>
          </DatabaseCacheInitializer>
        </AuthProvider>
      </QueryClientProvider>
      </ThemeProvider>
    </React.StrictMode>
  );
};

export default App;
